import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  CircularProgress,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import Layout from '../../components/Layout';
import QuoteDetailsDialog from '../../components/QuoteDetailsDialog';
import api from '../../services/api';
import { downloadQuotePdf } from '../../utils/quotePdf';

const CustomerQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quoteDetailOpen, setQuoteDetailOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await api.get('/customers/profile/quotes/');
      const quotesData = Array.isArray(response.data) ? response.data : [];
      setQuotes(quotesData);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      submitted: 'info',
      accepted: 'success',
      rejected: 'error',
      expired: 'warning',
    };
    return colors[status] || 'default';
  };

  const handleOpenQuoteDetails = (quote) => {
    setSelectedQuote(quote);
    setQuoteDetailOpen(true);
  };

  const handleCloseQuoteDetails = () => {
    setQuoteDetailOpen(false);
    setSelectedQuote(null);
  };

  const handleAcceptQuote = async (quote) => {
    try {
      await api.post(`/customers/quotes/${quote.id}/accept/`);
      handleCloseQuoteDetails();
      fetchQuotes();
      alert('Quote accepted! A new order has been created.');
    } catch (error) {
      console.error('Error accepting quote:', error);
      alert('Error accepting quote. Please try again.');
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Quotes</Typography>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Diesel (US gal)</TableCell>
                <TableCell>DEF (US gal)</TableCell>
                <TableCell>Dyed (US gal)</TableCell>
                <TableCell>Frequency</TableCell>
                <TableCell>Estimated Price</TableCell>
                <TableCell>Delivery Fee</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(quotes) && quotes.length > 0 ? (
                quotes.map((quote) => (
                  <TableRow
                    key={quote.id}
                    hover
                    onClick={() => handleOpenQuoteDetails(quote)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{quote.id.substring(0, 8)}</TableCell>
                    <TableCell>{parseFloat(quote.diesel_quantity || 0).toFixed(2)} gal</TableCell>
                    <TableCell>{parseFloat(quote.def_quantity || 0).toFixed(2)} gal</TableCell>
                    <TableCell>{parseFloat(quote.dyed_quantity || 0).toFixed(2)} gal</TableCell>
                    <TableCell>{quote.frequency}</TableCell>
                    <TableCell>${quote.estimated_price}</TableCell>
                    <TableCell>${parseFloat(quote.delivery_fee || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={quote.status}
                        color={getStatusColor(quote.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(quote.created_at).toLocaleDateString()}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<DownloadIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadQuotePdf(quote.id);
                          }}
                        >
                          PDF
                        </Button>
                        {quote.status === 'submitted' && (
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAcceptQuote(quote);
                            }}
                          >
                            Accept
                          </Button>
                        )}
                        {quote.status === 'draft' && (
                          <Typography variant="body2" color="textSecondary">
                            Draft
                          </Typography>
                        )}
                        {quote.status === 'accepted' && (
                          <Typography variant="body2" color="success.main">
                            Accepted
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No quotes found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <QuoteDetailsDialog
          open={quoteDetailOpen}
          onClose={handleCloseQuoteDetails}
          quote={selectedQuote}
          onAccept={handleAcceptQuote}
        />
      </Container>
    </Layout>
  );
};

export default CustomerQuotes;
